import {Injectable} from "@angular/core";

import {ApiClient, ApiClientConfiguration, ApiRequest} from "framework/services/api.client";
import {Lookup, UtilsService} from "framework/services/utils.service";
import {SwaggerDefinitionName} from "framework/ui/yaml-editor/editor/yaml-editor.component";
import {AppModelConstructor} from "framework/utils/app-model-constructor";
import {Memoizer} from "framework/utils/memoizers";

@Injectable()
export class SwaggerService
{
    private static readonly subclassDiscriminatorKey: string = "x-cs-subtypes";

    constructor(private m_configuration: ApiClientConfiguration,
                private m_apiClient: ApiClient)
    {}

    public sampleSwaggerDefinition<T>(definitionConstructor: SwaggerDefinition<T>,
                                      definitionName: SwaggerDefinitionName,
                                      pathsToOmit?: string[]): Promise<T>
    {
        return this.executeAgainstSwaggerDefinitions(definitionName, (definitions,
                                                                      definitionName) =>
        {
            const sample = this.generateSample(definitions, definitionName, definitions[definitionName], this.subclassDiscriminatorLookup(definitions));
            if (pathsToOmit?.length) this.removePaths(sample, pathsToOmit);
            return definitionConstructor.newInstance(sample);
        });
    }

    public validateObjectFn(definitionName: SwaggerDefinitionName,
                            pathsToOmit: string[]): Promise<(object: any) => YamlInvalidError>
    {
        return this.executeAgainstSwaggerDefinitions(definitionName, (definitions,
                                                                      definitionName) =>
        {
            return ((object: any) => this.validateAgainstSchema(object, definitions[definitionName], definitions, "", pathsToOmit));
        });
    }

    //--//

    public async getDefinitions(): Promise<Lookup<any>>
    {
        const swagger = await this.fetch();
        return swagger.definitions;
    }

    @Memoizer
    private async fetch(): Promise<any>
    {
        const requestOptions = new ApiRequest();
        requestOptions.setProduce("application/json");
        requestOptions.method = "GET";

        return this.m_apiClient.callWithOptions(this.m_configuration.apiHost + "/swagger.json", requestOptions);
    }

    @Memoizer
    private subclassDiscriminatorLookup(definitions: Lookup<any>): Lookup<string[]>
    {
        const lookup: Lookup<string[]> = {};

        for (const [defName, schema] of Object.entries(definitions || {}))
        {
            if (SwaggerService.subclassDiscriminatorKey in schema)
            {
                const subTypes: any[] = schema[SwaggerService.subclassDiscriminatorKey];
                if (subTypes?.length) lookup[defName] = subTypes.map(subType => subType.subtype_model);
            }
        }

        return lookup;
    }

    private async executeAgainstSwaggerDefinitions<T, O>(definitionName: string,
                                                         fn: (definitions: any,
                                                              definitionName: string) => O): Promise<O>
    {
        const swagger     = await this.fetch();
        const definitions = swagger?.definitions;
        if (definitions && definitionName)
        {
            if (!definitions[definitionName]) throw new Error(`Definition "${definitionName}" not found in swagger.json`);
            return fn(definitions, definitionName);
        }
        return null;
    }

    private generateSample(definitions: any,
                           definitionName: string,
                           schema: any,
                           subclassDiscriminators: Lookup<string[]>): any
    {
        if ("$ref" in schema)
        {
            const ref = schema["$ref"].replace(/^#\/definitions\//, "");

            let sample = this.generateSample(definitions, ref, definitions[ref], subclassDiscriminators);
            if (ref in subclassDiscriminators)
            {
                if (subclassDiscriminators[ref].includes(definitionName))
                {
                    sample.__type = definitionName;
                }
                else
                {
                    sample = null;
                }
            }

            return sample;
        }

        if ("allOf" in schema)
        {
            return Object.assign({}, ...schema.allOf.map((subSchema: any) => this.generateSample(definitions, null, subSchema, subclassDiscriminators)));
        }

        if (schema.type === "object")
        {
            const obj: Lookup<any> = {};
            if (schema.properties)
            {
                for (const [key, value] of Object.entries(schema.properties))
                {
                    obj[key] = this.generateSample(definitions, null, value, subclassDiscriminators);
                }
            }
            return obj;
        }

        if (schema.type === "array" && schema.items)
        {
            return [this.generateSample(definitions, null, schema.items, subclassDiscriminators)];
        }

        switch (schema.type)
        {
            case "string":
                return schema.example || "string";

            case "integer":
            case "number":
                return schema.example || 0;

            case "boolean":
                return schema.example || false;

            default:
                return null;
        }
    }

    private resolveSwaggerRef(ref: string,
                              definitions: any): any
    {
        const path = ref.replace(/^#\//, "")
                        .split("/");

        let obj = definitions;
        for (let i = 1; i < path.length; i++) obj = obj?.[path[i]];

        return obj;
    }

    private combineSchema(schema: any,
                          definitions: any): any
    {
        if (!schema.allOf) return schema;

        return schema.allOf.reduce((accum: any,
                                    part: any) =>
                                   {
                                       const resolvedPart = "$ref" in part ? this.resolveSwaggerRef(part["$ref"], definitions) : part;
                                       const merged       = this.combineSchema(resolvedPart, definitions);

                                       accum.properties = {...accum.properties, ...merged.properties};

                                       const required = new Set(accum.required);
                                       for (const r of merged.required || []) required.add(r);
                                       accum.required = Array.from(required);

                                       return accum;
                                   }, {
                                       type      : "object",
                                       properties: {},
                                       required  : []
                                   });
    }

    private isValidFormat(value: string,
                          format: string): boolean
    {
        switch (format)
        {
            case "date-time":
                return UtilsService.isNumber(Date.parse(value));

            default:
                return true; // Pass through for other formats
        }
    }

    private validateAgainstObjectSchema(value: any,
                                        schema: any,
                                        definitions: any,
                                        path: string,
                                        pathsToOmit: string[]): YamlInvalidError
    {
        const properties = schema.properties || {};
        const required   = schema.required || [];

        let splitPath: string[];
        for (const key of required)
        {
            if (!(key in value))
            {
                if (!splitPath) splitPath = path ? path.split(".") : [];

                for (const toOmit of pathsToOmit || [])
                {
                    let splitToOmit = toOmit.split(".");
                    if (splitPath.length + 1 == splitToOmit.length)
                    {
                        let i = 0;
                        for (; i < splitPath.length; i++)
                        {
                            const omitSegment = splitToOmit[i];
                            const pathSegment = splitPath[i];

                            // both are arrays or both have same ith segment
                            const matching = omitSegment[0] == pathSegment[0] && (omitSegment[0] == "[" || omitSegment == pathSegment);
                            if (!matching) break;
                        }
                        if (i == splitPath.length && splitToOmit[i] == key) return null;
                    }
                }

                return new YamlInvalidError(YamlInvalidType.MissingProperty, `Missing required property: "${key}"`, path);
            }
        }

        const discriminator: string = schema.discriminator;
        if (required.includes(discriminator) && SwaggerService.subclassDiscriminatorKey in schema)
        {
            const acceptableSubtypes = schema[SwaggerService.subclassDiscriminatorKey].map((subType: any) => subType.subtype_model);
            const error              = this.validateAgainstSchema(value[discriminator], {enum: acceptableSubtypes}, definitions, `${path}${discriminator}.`, pathsToOmit);
            if (error) return error;

            const subTypeSchema = this.getResolvedSchema(definitions[value[discriminator]], definitions);
            return this.validateAgainstObjectSchema(value, subTypeSchema, definitions, path, pathsToOmit);
        }

        for (const [key, propSchema] of Object.entries(properties))
        {
            if (key in value)
            {
                const error = this.validateAgainstSchema(value[key], propSchema, definitions, `${path}${key}.`, pathsToOmit);
                if (error) return error;
            }
        }

        return null;
    }

    private getResolvedSchema(schema: any,
                              definitions: any): any
    {
        if ("$ref" in schema) schema = this.resolveSwaggerRef(schema["$ref"], definitions);
        if ("allOf" in schema) schema = this.combineSchema(schema, definitions);
        return schema;
    }

    public validateAgainstSchema(value: any,
                                 schema: any,
                                 definitions: any,
                                 path: string,
                                 pathsToOmit: string[]): YamlInvalidError
    {
        schema = this.getResolvedSchema(schema, definitions);

        if (schema.type === "object")
        {
            if (typeof value !== "object" || Array.isArray(value) || value == null)
            {
                let valueType = Array.isArray(value) ? "array" : "";
                if (!valueType) valueType = value == null ? "nothing" : typeof value;
                return new YamlInvalidError(YamlInvalidType.InvalidValue, `Expected object, got ${valueType}`, path);
            }

            return this.validateAgainstObjectSchema(value, schema, definitions, path, pathsToOmit);
        }

        if (schema.type === "array")
        {
            if (!Array.isArray(value))
            {
                return new YamlInvalidError(YamlInvalidType.InvalidValue, `Expected array, got ${value == null ? "nothing" : typeof value}`, path);
            }

            const itemSchema = schema.items;
            for (let i = 0; i < value.length; i++)
            {
                const error = this.validateAgainstSchema(value[i], itemSchema, definitions, `${path}[${i}].`, pathsToOmit);
                if (error) return error;
            }
            return null;
        }

        if (schema.type === "string" && typeof value !== "string")
        {
            return new YamlInvalidError(YamlInvalidType.InvalidValue, `Expected string, got ${value == null ? "nothing" : typeof value}`, path);
        }
        if (schema.type === "integer" && !Number.isInteger(value))
        {
            return new YamlInvalidError(YamlInvalidType.InvalidValue, `Expected integer, got ${value}`, path);
        }
        if (schema.type === "number" && typeof value !== "number")
        {
            return new YamlInvalidError(YamlInvalidType.InvalidValue, `Expected number, got ${typeof value}`, path);
        }
        if (schema.type === "boolean" && typeof value !== "boolean")
        {
            return new YamlInvalidError(YamlInvalidType.InvalidValue, `Expected boolean, got ${typeof value}`, path);
        }
        if (schema.enum && !schema.enum.includes(value))
        {
            return new YamlInvalidError(YamlInvalidType.InvalidValue, `Invalid value: expected one of ${JSON.stringify(schema.enum)}, got "${value}"`, path);
        }
        if (schema.format && typeof value === "string" && !this.isValidFormat(value, schema.format))
        {
            return new YamlInvalidError(YamlInvalidType.InvalidValue, `Invalid format: expected ${schema.format}, got "${value}"`, path);
        }

        return null;
    }

    private removePaths(obj: any,
                        paths: string[])
    {
        for (const path of paths || []) this.removePath(obj, path);
    }

    private removePath(obj: any,
                       path: string)
    {
        let curr          = obj;
        const segments    = path?.split(".") || [];
        const numSegments = segments.length;
        for (let i = 0; i < numSegments - 1; i++)
        {
            const segment = segments[i];
            if (segment == "[]")
            {
                if (Array.isArray(curr))
                {
                    const pathSuffix = segments.slice(i + 1)
                                               .join(".");
                    for (const element of curr) this.removePath(element, pathSuffix);
                    return;
                }
            }
            else if (segment in curr)
            {
                curr = curr[segment];
            }
        }

        const lastSegment = segments[numSegments - 1];
        if (curr && lastSegment in curr) delete curr[lastSegment];
    }
}

export interface SwaggerDefinition<T> extends AppModelConstructor<T>
{}

export class YamlInvalidError
{
    readonly path: string[];

    constructor(public readonly type: YamlInvalidType,
                public readonly message: string,
                path: string)
    {
        this.path = path.split(".");
    }
}

export enum YamlInvalidType
{
    InvalidValue    = "InvalidValue",
    MissingProperty = "MissingProperty"
}